import React, { useMemo, useState } from "react";
import Input from "../../components/common/Input/Input";
import Button from "../../components/common/Button/Button";
import { useTranslation } from "react-i18next";
import Axios from "../../redux/axiosInstance";
import { changePasswordEndpoints } from "../../api";
import { toast } from "sonner";
import useLanguageTranslate from "../../hooks/useLanguageTranslate";
import StickyActionBar from "../../components/common/stickyActionBar/StickyActionBar";
import ValidationItem from "../../components/common/ValidationItem/ValidationItem";
import { getClientSource } from "../../utils/getClientSource";

// Same rules and wording as the sign-up checklist (CreatePasswordPage).
const passwordRules = (value = "") => [
  { is_valid: value.length >= 12, msg: { en: "At least 12 characters", ar: "على الأقل 12 حرفًا" } },
  { is_valid: /[A-Z]/.test(value), msg: { en: "1 uppercase letter", ar: "حرف كبير واحد" } },
  { is_valid: /[a-z]/.test(value), msg: { en: "1 lowercase letter", ar: "حرف صغير واحد" } },
  { is_valid: /[0-9]/.test(value), msg: { en: "1 number", ar: "رقم واحد" } },
  { is_valid: /[!@#$%^&*(),.?":{}|<>]/.test(value), msg: { en: "1 symbol", ar: "رمز واحد" } },
];

/**
 * Change password form, shared by desktop Profile Settings and the mobile
 * /change-password page.
 *
 * The new password's rules show as a live checklist (as in sign-up) rather
 * than a paragraph-long error re-shown on every keystroke; the error itself
 * only appears if the customer presses the button with rules still unmet.
 * Loading shows in the button. On success the fields clear and `onSuccess`
 * runs (mobile uses it to go back). `pinActionOnMobile` pins the button.
 */
const ChangePassword = ({ onSuccess, pinActionOnMobile = false }) => {
  const [formData, setFormData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmNewPassword: "",
  });
  const { t } = useTranslation();
  const translate = useLanguageTranslate();
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: "",
    }));
    // setErrors({})

    if (name == "confirmNewPassword") {
      if (value != formData.newPassword) {
        setErrors((prevErrors) => ({
          ...prevErrors,
          confirmNewPassword: translate(
            "Passwords do not match",
            "كلمتا المرور غير متطابقتين."
          ),
        }));
      } else {
        setErrors((prevErrors) => ({
          ...prevErrors,
          confirmNewPassword: "",
        }));
      }
    }
  };

  const validateForm = () => {
    const newErrors = {};

    const minLength = 12; // new passwords: 12 characters minimum
    const hasUppercase = /[A-Z]/.test(formData?.newPassword);
    const hasLowercase = /[a-z]/.test(formData?.newPassword);
    const hasNumber = /[0-9]/.test(formData?.newPassword);
    const hasSymbol = /[!@#$%^&*(),.?":{}|<>]/.test(formData?.newPassword);

    if (!formData?.currentPassword) {
      newErrors.currentPassword = t("validation.required");
    }
    if (!formData?.newPassword) {
      newErrors.newPassword = t("validation.required");
    } else if (
      formData?.newPassword.length < minLength ||
      !hasUppercase ||
      !hasLowercase ||
      !hasNumber ||
      !hasSymbol
    ) {
      newErrors.newPassword = translate(
        "Password should be at least 12 characters long and should contain one uppercase letter, one lowercase letter, one symbol, and one number.",
        "يجب أن تكون كلمة المرور مكونة من 12 حرفًا على الأقل وتحتوي على حرف كبير واحد، وحرف صغير واحد، ورمز واحد، ورقم واحد."
      );
    }
    if (!formData?.confirmNewPassword) {
      newErrors.confirmNewPassword = t("validation.required");
    }
    if (
      formData?.newPassword &&
      formData?.newPassword != formData?.confirmNewPassword
    ) {
      newErrors.confirmNewPassword = translate(
        "Passwords do not match",
        "كلمتا المرور غير متطابقتين."
      );
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length == 0;
  };

  const rules = useMemo(
    () => passwordRules(formData?.newPassword),
    [formData?.newPassword]
  );

  // Ready only when it can succeed: current password entered, every rule met,
  // and the confirmation matching. Until then the button stays disabled, as
  // on the other password steps, instead of looking pressable on an empty form.
  const isComplete =
    Boolean(formData?.currentPassword) &&
    rules.every((rule) => rule.is_valid) &&
    formData?.confirmNewPassword === formData?.newPassword;

  const updatePassApiHandler = async () => {
    const { currentPassword, newPassword } = formData;
    const payload = {
      Password: currentPassword,
      NewPassword: newPassword,
      Channel: getClientSource(),
    };
    try {
      setIsLoading(true);
      const res = await Axios.post(
        changePasswordEndpoints?.updatepassword,
        payload
      );
      setIsLoading(false);
      if (res?.status == 200) {
        toast.success(
          translate(
            "Password updated successfully",
            "تم تحديث كلمة المرور بنجاح"
          )
        );
        setFormData({ currentPassword: "", newPassword: "", confirmNewPassword: "" });
        setErrors({});
        onSuccess?.();
      } else {
        toast.error(translate(res?.ErrorMessageEn, res?.ErrorMessageAr));
      }
    } catch (err) {
      setIsLoading(false);
      let response = err?.response?.data;
      toast.error(
        translate(
          response?.ErrorMessageEn || "Something went wrong try again",
          response?.ErrorMessageAr
        )
      );
    }
  };

  const changePassword = async () => {
    const { currentPassword } = formData;
    if (validateForm()) {
      try {
        setIsLoading(true);
        const response = await Axios.post(
          changePasswordEndpoints?.confirmpassword,
          { Password: currentPassword }
        );
        setIsLoading(false);
        if (response?.status == 200) {
          updatePassApiHandler();
        } else {
          toast.error(
            translate(response?.ErrorMessageEn, response?.ErrorMessageAr)
          );
        }
      } catch (err) {
        setIsLoading(false);
        let response = err?.response?.data;
        toast.error(
          translate(
            response?.ErrorMessageEn || "Something went wrong try again",
            response?.ErrorMessageAr
          )
        );
      }
    }
  };

  return (
    <div>
      <div className="yourProfile mb-4">
        <div className="inputGrid px-0 pt-6 pb-0 flex flex-col md:flex-row md:space-x-4">
          <Input
            label={t("profileSettings.current_password")}
            type="password"
            name="currentPassword"
            value={formData?.currentPassword}
            setFormData={setFormData}
            handleChange={handleChange}
            error={errors?.currentPassword}
          />
        </div>
        <div className="inputGrid px-0 pt-6 flex flex-col md:flex-row md:space-x-4 rtl:gap-3 rtl:mb-4">
          <Input
            label={t("profileSettings.new_password")}
            type="password"
            name="newPassword"
            value={formData?.newPassword}
            setFormData={setFormData}
            handleChange={handleChange}
            error={errors?.newPassword}
          />
          <Input
            label={t("profileSettings.confirm_new_password")}
            type="password"
            name="confirmNewPassword"
            value={formData?.confirmNewPassword}
            setFormData={setFormData}
            handleChange={handleChange}
            error={errors?.confirmNewPassword}
          />
        </div>
        <div className="pass_valid_cont mt-2 mb-6 flex flex-col items-start gap-2 self-stretch rounded-[10px] bg-[#F6F8F9] p-4">
          <h4 className="text-[#1A2024] text-sm font-medium leading-5">
            {translate("Your new password must have:", "يجب أن تحتوي كلمة المرور الجديدة على:")}
          </h4>
          {rules.map((rule) => (
            <ValidationItem
              key={rule.msg.en}
              is_validated={rule.is_valid}
              is_pending={!formData?.newPassword}
              msg={rule.msg}
            />
          ))}
        </div>
        <div className={pinActionOnMobile ? "" : "flex justify-end ipad:justify-start"}>
          <StickyActionBar pinned={pinActionOnMobile}>
            <Button
              label={t("profileSettings.update_password")}
              className={
                // Colour eases (not snaps) as the form becomes valid; a small
                // press scale confirms the tap.
                `${
                  pinActionOnMobile
                    ? "primary-button !mb-0"
                    : "flex flex-col items-center gap-2.5 px-12 py-2 rounded-[10px]"
                } transition-[background-color,transform] duration-150 ease-out active:scale-[0.98] disabled:active:scale-100`
              }
              isLoading={isLoading}
              disabled={isLoading || !isComplete}
              onSubmit={changePassword}
            />
          </StickyActionBar>
        </div>
      </div>
    </div>
  );
};

export default ChangePassword;
